/**
 * `/calendar` — the firm calendar: every client's reminders and
 * appointments plus teammates' synced Google/Outlook events, in the same
 * month / week / day / agenda views as a client's Reminders tab. A Source
 * toggle narrows to CaseBlink or synced entries; a new entry picks a client
 * or, with none, belongs to the firm.
 */
import dayjs from "dayjs";
import { ArrowUpRight, CalendarRange, Plug } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import {
  useWatch,
  type SubmitHandler,
  type UseFormReturn
} from "react-hook-form";
import { useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";

import { ComboboxField } from "@/components/form/ComboboxField";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useTeamMembers } from "@/features/agents/useTeamMembers";
import { CONNECTORS_PATH } from "@/features/connectors/routes";
import {
  PanelError,
  PanelLoading
} from "@/features/clientProfile/components/PanelState";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import {
  draftForSlot,
  filterReminders,
  isCalendarView,
  mergeDuplicateEvents,
  toCalendarEvents,
  type CalendarEvent,
  type CalendarView
} from "..";
import { AgendaView } from "../components/AgendaView";
import { CalendarToolbar, FilterSection } from "../components/CalendarToolbar";
import { EventSheet } from "../components/EventSheet";
import { ExternalEventPopover } from "../components/ExternalEventPopover";
import { MonthView } from "../components/MonthView";
import { TimeGridView } from "../components/TimeGridView";
import {
  rangeForView,
  useCalendarEvents,
  useFirmReminders,
  useMergedCalendar
} from "../queries";
import {
  DEFAULT_REMINDER_FILTERS,
  type ReminderDraft,
  type ReminderFilters
} from "../remindersModel";
import { useCalendarConnection } from "../useCalendarConnection";
import { useCalendarState } from "../useCalendarState";
import { useFirmClients } from "../useFirmClients";
import {
  useFirmReminderCrud,
  type FirmReminderDraft
} from "../useFirmReminderCrud";

export const FIRM_CALENDAR_VIEW_KEY = "firm.calendar.view";

export type SourceFilter = "all" | "caseblink" | "synced";

const SOURCE_OPTIONS: ReadonlyArray<{ id: SourceFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "caseblink", label: "CaseBlink" },
  { id: "synced", label: "Synced" }
];

const SourceToggle: FC<{
  value: SourceFilter;
  onChange: (v: SourceFilter) => void;
}> = ({ value, onChange }) => (
  <FilterSection label="Source">
    <ToggleGroup
      type="single"
      variant="outline"
      size="sm"
      value={value}
      onValueChange={(v) => v && onChange(v as SourceFilter)}
      aria-label="Source"
      className="w-full"
      data-testid="source-toggle"
    >
      {SOURCE_OPTIONS.map((o) => (
        <ToggleGroupItem key={o.id} value={o.id} className="flex-1 text-xs">
          {o.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  </FilterSection>
);

const initialAnchor = (params: URLSearchParams): Date => {
  const raw = params.get("date");
  const d = raw ? dayjs(raw) : null;
  return d?.isValid() ? d.toDate() : new Date();
};

export const FirmCalendarPage: FC = () => {
  const [params] = useSearchParams();
  const uid = useSelector(lawyerSelectors.selectUid) as string | null;
  const { members } = useTeamMembers();
  const teamOptions = useMemo(
    () => members.map((m) => ({ value: m.uid, label: m.label })),
    [members]
  );
  const viewParam = params.get("view");
  const calendar = useCalendarState(initialAnchor(params), {
    storageKey: FIRM_CALENDAR_VIEW_KEY,
    defaultView: isCalendarView(viewParam) ? viewParam : "month"
  });
  const { setView } = calendar;
  useEffect(() => {
    if (isCalendarView(viewParam)) setView(viewParam);
  }, [setView, viewParam]);

  const [filters, setFilters] = useState<ReminderFilters>(
    DEFAULT_REMINDER_FILTERS
  );
  const [source, setSource] = useState<SourceFilter>("all");
  const [popover, setPopover] = useState<CalendarEvent | null>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const range = useMemo(
    () => rangeForView(calendar.view, calendar.anchor),
    [calendar.view, calendar.anchor]
  );
  const reminders = useFirmReminders(range);
  const synced = useCalendarEvents(range, { enabled: source !== "caseblink" });
  const connection = useCalendarConnection();
  const clients = useFirmClients();
  const crud = useFirmReminderCrud();
  const pickedClient = useWatch({
    control: crud.form.control,
    name: "clientId"
  });

  const reminderEvents = useMemo(() => {
    const visible = filterReminders(reminders.reminders, filters);
    const names: Record<string, string> = {};
    for (const r of visible) {
      if (r.client_name && r.foreign_national_id)
        names[r.foreign_national_id] = r.client_name;
    }
    return toCalendarEvents(visible, names);
  }, [filters, reminders.reminders]);
  const externalEvents = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const merged = mergeDuplicateEvents(synced.events);
    return q ? merged.filter((e) => e.title.toLowerCase().includes(q)) : merged;
  }, [filters.search, synced.events]);
  const events = useMergedCalendar(reminderEvents, externalEvents, {
    showReminders: source !== "synced",
    showExternal: source !== "caseblink"
  });

  const openNew = useCallback(
    () =>
      crud.openCreate(
        draftForSlot(calendar.anchor, false, {
          assignedToUserIds: uid ? [uid] : []
        }) as Partial<FirmReminderDraft>
      ),
    [calendar.anchor, crud, uid]
  );
  const openAt = useCallback(
    (start: Date, allDay: boolean) =>
      crud.openCreate(
        draftForSlot(start, allDay, {
          assignedToUserIds: uid ? [uid] : []
        }) as Partial<FirmReminderDraft>
      ),
    [crud, uid]
  );
  const openEvent = useCallback(
    (event: CalendarEvent) => {
      if (event.source !== "reminder") {
        setPopover(event);
        return;
      }
      const record = reminders.reminders.find(
        (r) => r.reminder_id === event.reminder?.reminder_id
      );
      if (record) crud.openEdit(record);
    },
    [crud, reminders.reminders]
  );
  const nameOf = useCallback(
    (id: string) => members.find((m) => m.uid === id)?.label,
    [members]
  );

  const calendars = synced.sources.filter((s) => s.status === "ok").length;
  const showConnect =
    !connection.isLoading && connection.enabled && !connection.connected;

  const renderView = () => {
    if (reminders.error && reminders.reminders.length === 0) {
      return (
        <PanelError
          title="Couldn't load the calendar"
          message={reminders.error}
          onRetry={() => {
            reminders.reload().catch(() => undefined);
          }}
        />
      );
    }
    if (reminders.isLoading && reminders.reminders.length === 0) {
      return <PanelLoading text="Loading calendar…" />;
    }
    const highlightedId = popover?.id ?? null;
    switch (calendar.view) {
      case "week":
      case "day":
        return (
          <TimeGridView
            mode={calendar.view}
            anchor={calendar.anchor}
            events={events}
            highlightedId={highlightedId}
            onOpenEvent={openEvent}
            onCreateAt={openAt}
            now={now}
          />
        );
      case "agenda":
        return (
          <AgendaView
            anchor={calendar.anchor}
            events={events}
            highlightedId={highlightedId}
            onOpenEvent={openEvent}
            onCreate={openNew}
            now={now}
          />
        );
      case "month":
      default:
        return (
          <MonthView
            anchor={calendar.anchor}
            events={events}
            highlightedId={highlightedId}
            onOpenEvent={openEvent}
            onCreateAt={openAt}
            now={now}
          />
        );
    }
  };

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface"
      data-testid="firm-calendar-page"
    >
      <CalendarToolbar
        label="Firm Calendar"
        viewControl="segment"
        view={calendar.view}
        anchor={calendar.anchor}
        onView={calendar.setView}
        onToday={calendar.today}
        onPrev={calendar.prev}
        onNext={calendar.next}
        filters={filters}
        onFilters={setFilters}
        teamOptions={teamOptions}
        onNew={openNew}
        extraFilters={<SourceToggle value={source} onChange={setSource} />}
        extraFilterCount={source === "all" ? 0 : 1}
        onClearExtraFilters={() => setSource("all")}
      />

      <div className="relative min-h-0 flex-1 bg-surface">
        {renderView()}
        <ExternalEventPopover
          event={popover}
          onOpenChange={(open) => {
            if (!open) setPopover(null);
          }}
          nameOf={nameOf}
        >
          <span className="absolute top-2 left-1/2 size-0" aria-hidden />
        </ExternalEventPopover>
      </div>

      <footer
        className="flex h-9 shrink-0 items-center gap-2 border-t border-line-2 pr-3 pl-5 text-sm text-fg-3"
        data-testid="firm-calendar-footer"
      >
        <CalendarRange className="size-3.5" />
        {showConnect ? (
          <span className="truncate">
            Connect Google or Outlook to see your calendar alongside the firm's
            reminders.
          </span>
        ) : (
          <span className="truncate" data-testid="firm-calendar-synced">
            {calendars
              ? `Synced from ${calendars} calendar${calendars === 1 ? "" : "s"}`
              : "No synced calendars yet"}
          </span>
        )}
        <span className="flex-1" />
        {showConnect ? (
          <Button size="xs" variant="ghost" asChild>
            <Link to={CONNECTORS_PATH} data-testid="firm-calendar-connect">
              Open Connectors
              <ArrowUpRight />
            </Link>
          </Button>
        ) : null}
        {!showConnect ? (
          <Button
            size="xs"
            variant="ghost"
            onClick={() => void synced.refresh()}
            disabled={synced.isRefreshing}
            data-testid="firm-calendar-resync"
          >
            <Plug className="size-3" />
            {synced.isRefreshing ? "Syncing…" : "Sync now"}
          </Button>
        ) : null}
      </footer>

      <EventSheet
        open={crud.editor.mode !== "closed"}
        onOpenChange={(open) => {
          if (!open) crud.close();
        }}
        // The firm draft is a superset of the sheet's (`clientId` extra); the
        // sheet only touches the shared fields.
        form={crud.form as unknown as UseFormReturn<ReminderDraft>}
        onSubmit={crud.submit as unknown as SubmitHandler<ReminderDraft>}
        record={crud.editingRecord}
        isSaving={crud.isSaving}
        isDeleting={crud.isDeleting}
        onDelete={() => {
          crud.deleteCurrent().catch(() => undefined);
        }}
        onSetStatus={(status) => {
          crud.setStatus(status).catch(() => undefined);
        }}
        isStatusBusy={crud.isStatusBusy}
        clientField={
          <ComboboxField
            control={crud.form.control}
            name="clientId"
            label="Client"
            description="Leave empty for a firm entry, like a team meeting"
            options={clients.options}
            placeholder={
              clients.isLoading ? "Loading clients…" : "No client (firm entry)"
            }
            searchPlaceholder="Search clients…"
            emptyText="No matching clients."
            disabled={crud.editor.mode === "edit"}
          />
        }
        subtitle={
          <span className="text-xs text-muted-foreground">
            Visible to the whole firm
          </span>
        }
        canShareWithClient={Boolean(pickedClient?.trim())}
      />
    </div>
  );
};
